<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import PrimaryButton from './PrimaryButton.vue';
import OutlineButton from './OutlineButton.vue';

export type ModalType = 'success' | 'error' | 'info' | 'warning' | 'default';
export type ModalSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl';

export interface ModalProps {
  title?: string;
  description?: string;
  icon?: string;
  iconClass?: string;
  type?: ModalType;
  size?: ModalSize;
  static?: boolean;
  closeOnConfirm?: boolean;
  closeOnCancel?: boolean;
  hideActions?: boolean;
  showCloseButton?: boolean;
  centerContent?: boolean;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonDisabled?: boolean;
  invertButtons?: boolean;
  centered?: boolean;
  isLoading?: boolean;
}

const { t } = useTranslations();

const props = withDefaults(defineProps<ModalProps>(), {
  title: '',
  description: '',
  icon: '',
  iconClass: '',
  type: 'default',
  size: 'md',
  hideActions: false,
  static: false,
  closeOnConfirm: true,
  closeOnCancel: true,
  showCloseButton: true,
  centerContent: false,
  showConfirmButton: true,
  showCancelButton: true,
  confirmButtonText: '',
  cancelButtonText: '',
  invertButtons: false,
  centered: false,
  isLoading: false,
});

const isOpen = defineModel<boolean>({ required: true });

const slots = defineSlots<{
  header(): void;
  icon(): void;
  default(): void;
  description(): void;
  footer(): void;
  confirmButton(): void;
  cancelButton(): void;
}>();

const emit = defineEmits<{
  close: [];
  confirm: [];
  cancel: [];
}>();

function closeModal() {
  isOpen.value = false;
  emit('close');
}

function handleConfirm() {
  emit('confirm');
  if (!props.static && props.closeOnConfirm) closeModal();
}

function handleCancel() {
  emit('cancel');
  if (!props.static && props.closeOnCancel) closeModal();
}

const maxWidthClass = computed(() => `max-w-${props.size}`);

const defaultIcon = computed(() => {
  if (props.icon) return props.icon;
  switch (props.type) {
    case 'success':
      return 'heroicons:check-circle';
    case 'error':
      return 'heroicons:x-circle';
    case 'info':
      return 'heroicons:information-circle';
    case 'warning':
      return 'heroicons:exclamation-triangle';
    default:
      return '';
  }
});

const iconColorClass = computed(() => {
  switch (props.type) {
    case 'success':
      return 'text-system-success-green bg-system-success-green/10';
    case 'error':
      return 'text-system-error-red bg-system-error-red/10';
    case 'warning':
      return 'text-system-warning-yellow bg-system-warning-yellow/10';
    case 'info':
      return 'text-brand-primary bg-brand-primary/10';
    default:
      return 'text-brand-primary bg-brand-primary/10';
  }
});

const contentAlignClass = computed(() => {
  if (props.centered) return 'text-center';
  return props.centerContent ? 'text-center sm:text-left' : 'text-left';
});

const hasIcon = computed(() => !!slots.icon || !!defaultIcon.value);
</script>

<template>
  <TransitionRoot as="template" @after-leave="emit('close')" :show="isOpen">
    <Dialog
      :static="static"
      :unmount="true"
      class="relative z-[100]"
      @close="static ? () => {} : closeModal()"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-semantic-background-secondary/80 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-20 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          :class="{ '!items-center': centered }"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              :class="[
                maxWidthClass,
                'relative m-5 w-full overflow-hidden rounded-xl bg-white p-6 text-left shadow-modal ring-1 ring-semantic-background-secondary transition-all sm:my-8',
                { 'text-center': centered },
              ]"
            >
              <!-- Close button -->
              <div v-if="showCloseButton && !static" class="absolute right-0 top-0 z-10 pr-4 pt-4">
                <button
                  type="button"
                  class="rounded-md bg-white text-semantic-text-primary hover:text-brand-primary focus:outline-none"
                  @click="closeModal"
                >
                  <span class="sr-only">Close</span>
                  <IconifyIcon class="size-6" icon="heroicons:x-mark-solid" aria-hidden="true" />
                </button>
              </div>

              <!-- Content area -->
              <div>
                <!-- Custom header slot -->
                <slot name="header">
                  <!-- Default Header Implementation -->
                  <div :class="{ 'flex items-center': !centered }">
                    <!-- Icon Section -->
                    <div
                      v-if="hasIcon"
                      :class="[
                        'mx-auto flex size-12 shrink-0 items-center justify-center rounded-full',
                        iconColorClass,
                        props.iconClass,
                        {
                          'mb-4': centered,
                          'mx-0 mr-4 size-10': !centered,
                        },
                      ]"
                    >
                      <slot name="icon">
                        <IconifyIcon :icon="defaultIcon" class="size-6" aria-hidden="true" />
                      </slot>
                    </div>

                    <!-- Text Section (Title & Description) -->
                    <div
                      :class="[
                        'mt-3',
                        contentAlignClass,
                        {
                          '!mt-0': !centered && hasIcon,
                          'sm:mt-5': centered,
                        },
                      ]"
                    >
                      <DialogTitle v-if="title" as="h3" :class="['text-lg font-medium leading-6']">
                        {{ title }}
                      </DialogTitle>
                      <div class="mt-1" v-if="!!slots.description || description">
                        <slot name="description">
                          <p class="text-sm text-semantic-text-secondary">{{ description }}</p>
                        </slot>
                      </div>
                    </div>
                  </div>
                </slot>

                <!-- Main content slot -->
                <div
                  v-if="!!slots.default"
                  class="mt-4"
                  :class="[contentAlignClass, { 'text-center': centered }]"
                >
                  <slot></slot>
                </div>
              </div>

              <!-- Footer with buttons -->
              <div
                v-if="!hideActions"
                class="mt-5 flex gap-2"
                :class="[
                  'flex-col-reverse xs:flex-row',
                  {
                    'justify-end': !centered,
                    'justify-center': centered,
                  },
                ]"
              >
                <slot name="footer">
                  <!-- Cancel Button Wrapper -->
                  <template v-if="invertButtons">
                    <!-- Confirm Button Wrapper -->
                    <div
                      v-if="showConfirmButton"
                      class="w-full"
                      :class="{ 'md:w-auto': !centered }"
                    >
                      <slot name="confirmButton">
                        <OutlineButton
                          size="sm"
                          :isLoading="isLoading"
                          :disabled="confirmButtonDisabled"
                          @click="handleConfirm"
                          class="w-full min-w-24"
                          :color="type === 'info' ? 'default' : type"
                        >
                          {{ confirmButtonText || t('common.confirm') }}
                        </OutlineButton>
                      </slot>
                    </div>

                    <div v-if="showCancelButton" class="w-full" :class="{ 'md:w-auto': !centered }">
                      <slot name="cancelButton">
                        <PrimaryButton
                          size="sm"
                          @click="handleCancel"
                          :disabled="isLoading"
                          class="w-full min-w-24"
                          :color="type === 'info' ? 'default' : type"
                        >
                          {{ cancelButtonText || t('common.cancel') }}
                        </PrimaryButton>
                      </slot>
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="showCancelButton" class="w-full" :class="{ 'md:w-auto': !centered }">
                      <slot name="cancelButton">
                        <OutlineButton
                          size="sm"
                          @click="handleCancel"
                          :disabled="isLoading"
                          class="w-full min-w-24"
                          :color="type === 'info' ? 'default' : type"
                        >
                          {{ cancelButtonText || t('common.cancel') }}
                        </OutlineButton>
                      </slot>
                    </div>

                    <!-- Confirm Button Wrapper -->
                    <div
                      v-if="showConfirmButton"
                      class="w-full"
                      :class="{ 'md:w-auto': !centered }"
                    >
                      <slot name="confirmButton">
                        <PrimaryButton
                          size="sm"
                          :isLoading="isLoading"
                          :disabled="confirmButtonDisabled"
                          @click="handleConfirm"
                          class="w-full min-w-24"
                          :color="type === 'info' ? 'default' : type"
                        >
                          {{ confirmButtonText || t('common.confirm') }}
                        </PrimaryButton>
                      </slot>
                    </div>
                  </template>
                </slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
